import type {
  NotificationPreference,
  NotificationPreferencePatch,
} from '../api.js';
import {
  Badge,
  Button,
  DataView,
  Empty,
  IcnAlert,
  IcnBell,
  IcnMail,
  IcnRefresh,
  IcnSave,
  IcnShield,
  IcnZap,
  Input,
  Select,
  SettingRow,
  StatusBar,
  Switch,
  useToast,
} from '@viquard/doc-ui';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionHeader } from '@/features/settings/views';
import {
  useNotificationPreferences,
  useQuietHours,
  useRegisterPushSubscription,
  useUnregisterPushSubscription,
  useUpdateQuietHours,
  useUpdateNotificationPreferences,
} from '../api.js';
import { humanizeNotificationKind } from '../format.js';
import { NotificationDigestPreview } from './notification-digest-preview.js';
import { NotificationPushPanel } from './notification-push-panel.js';
import {
  readBrowserPushState,
  registerBrowserPush,
  unregisterBrowserPush,
  type BrowserPushState,
} from '../push-registration.js';

type Channel = NotificationPreference['channel'];
type DigestCadence = NotificationPreference['digest'];

const CHANNELS: readonly Channel[] = ['in-app', 'email', 'push'];
const DEFAULT_QUIET_HOURS = {
  enabled: false,
  start: '22:00',
  end: '07:00',
  tzid:
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
      : 'UTC',
};

interface PreferenceGroup {
  kind: string;
  schemaVersion: number | null;
  channels: Partial<Record<Channel, NotificationPreference>>;
}

export function NotificationPreferencesSection(): ReactNode {
  const { t } = useTranslation('settings');
  const toast = useToast();
  const preferences = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();
  const quietHours = useQuietHours();
  const updateQuietHours = useUpdateQuietHours();
  const registerPush = useRegisterPushSubscription();
  const unregisterPush = useUnregisterPushSubscription();
  const [quietDraft, setQuietDraft] = useState(DEFAULT_QUIET_HOURS);
  const [pushState, setPushState] = useState<BrowserPushState>({
    supported: false,
    reason: 'server',
    permission: 'unsupported',
    endpoint: null,
  });
  const [pushStateLoading, setPushStateLoading] = useState(true);
  const digestOptions = useMemo(
    () =>
      [
        { value: 'off', label: t('notifications.digestOptions.off') },
        { value: 'daily', label: t('notifications.digestOptions.daily') },
        { value: 'weekly', label: t('notifications.digestOptions.weekly') },
      ] satisfies { value: DigestCadence; label: string }[],
    [t],
  );
  const notificationKindDescription = useCallback(
    (kind: string) =>
      t(`notifications.kindDescriptions.${kind}`, {
        defaultValue: t('notifications.kindDescriptions.fallback', { kind }),
      }),
    [t],
  );

  useEffect(() => {
    if (!quietHours.data) return;
    setQuietDraft(quietHours.data.quietHours);
  }, [quietHours.data]);

  const refreshPushState = useCallback(async () => {
    setPushStateLoading(true);
    try {
      setPushState(await readBrowserPushState());
    } catch {
      setPushState({
        supported: false,
        reason: 'server',
        permission: 'unsupported',
        endpoint: null,
      });
    } finally {
      setPushStateLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshPushState();
  }, [refreshPushState]);

  const updatePreference = async (
    pref: NotificationPreference,
    next: Pick<NotificationPreferencePatch, 'enabled'> &
      Partial<Pick<NotificationPreferencePatch, 'digest'>>,
  ): Promise<void> => {
    const patch: NotificationPreferencePatch = {
      kind: pref.kind,
      channel: pref.channel,
      enabled: next.enabled,
      digest: next.digest ?? pref.digest,
    };
    try {
      await update.mutateAsync({ preferences: [patch] });
      toast.success({ title: t('notifications.saved'), duration: 3000 });
    } catch {
      toast.danger({ title: t('notifications.saveFailed'), duration: 5000 });
    }
  };

  const saveQuietHours = async (): Promise<void> => {
    try {
      await updateQuietHours.mutateAsync(quietDraft);
      toast.success({ title: t('notifications.quietHours.saved'), duration: 3000 });
    } catch {
      toast.danger({ title: t('notifications.quietHours.saveFailed'), duration: 5000 });
    }
  };

  const enablePush = async (): Promise<void> => {
    try {
      const payload = await registerBrowserPush();
      await registerPush.mutateAsync(payload);
      await refreshPushState();
      toast.success({ title: t('notifications.push.enabledToast'), duration: 3000 });
    } catch {
      toast.danger({ title: t('notifications.push.enableFailed'), duration: 5000 });
    }
  };

  const disablePush = async (): Promise<void> => {
    try {
      const endpoint = await unregisterBrowserPush();
      if (endpoint) await unregisterPush.mutateAsync({ endpoint });
      await refreshPushState();
      toast.success({ title: t('notifications.push.disabledToast'), duration: 3000 });
    } catch {
      toast.danger({ title: t('notifications.push.disableFailed'), duration: 5000 });
    }
  };

  return (
    <section data-testid="settings-notifications" className="flex flex-col gap-4">
      <SectionHeader
        icon={<IcnBell size={18} />}
        title={t('notifications.title')}
        subtitle={t('notifications.subtitle')}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.82fr)]">
        <section
          className="flex flex-col gap-4"
          style={{
            padding: 'var(--vq-space-5)',
            background: 'var(--vq-paper-raised)',
            border: '1px solid var(--vq-rule)',
            borderRadius: 'var(--vq-radius-lg)',
          }}
          aria-label={t('notifications.quietHours.title')}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="m-0 flex items-center gap-2 text-base font-semibold">
                <IcnShield size={16} /> {t('notifications.quietHours.title')}
              </h3>
              <p className="m-0 mt-1 text-sm text-[color:var(--vq-text-muted)]">
                {t('notifications.quietHours.subtitle')}
              </p>
            </div>
            <Switch
              size="sm"
              checked={quietDraft.enabled}
              disabled={quietHours.isPending || updateQuietHours.isPending}
              label={t('notifications.quietHours.enabled')}
              onCheckedChange={(enabled) => {
                setQuietDraft((current) => ({ ...current, enabled }));
              }}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              {t('notifications.quietHours.start')}
              <Input
                type="time"
                value={quietDraft.start}
                onValueChange={(start) => {
                  setQuietDraft((current) => ({ ...current, start }));
                }}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              {t('notifications.quietHours.end')}
              <Input
                type="time"
                value={quietDraft.end}
                onValueChange={(end) => {
                  setQuietDraft((current) => ({ ...current, end }));
                }}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              {t('notifications.quietHours.tzid')}
              <Input
                value={quietDraft.tzid}
                onValueChange={(tzid) => {
                  setQuietDraft((current) => ({ ...current, tzid }));
                }}
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Badge tone={quietDraft.enabled ? 'success' : 'neutral'} variant="soft" size="sm">
              {quietDraft.enabled
                ? t('notifications.quietHours.activeWindow', {
                    start: quietDraft.start,
                    end: quietDraft.end,
                  })
                : t('notifications.quietHours.inactive')}
            </Badge>
            <Button
              size="sm"
              variant="primary"
              iconLeft={<IcnSave size={14} />}
              loading={updateQuietHours.isPending}
              disabled={quietHours.isPending}
              onClick={() => {
                void saveQuietHours();
              }}
            >
              {t('notifications.quietHours.save')}
            </Button>
          </div>
        </section>

        <NotificationPushPanel
          pushState={pushState}
          pushStateLoading={pushStateLoading}
          registerPending={registerPush.isPending}
          unregisterPending={unregisterPush.isPending}
          onEnable={() => { void enablePush(); }}
          onDisable={() => { void disablePush(); }}
          onRefresh={() => { void refreshPushState(); }}
        />
      </div>

      <DataView
        loading={preferences.isPending}
        error={preferences.error ?? undefined}
        data={preferences.data}
        isEmpty={(data) => data === undefined || data.preferences.length === 0}
        emptyFallback={
          <Empty
            icon={<IcnBell size={18} />}
            title={t('notifications.emptyTitle')}
            description={t('notifications.emptyDescription')}
          />
        }
        errorFallback={
          <Empty
            icon={<IcnAlert size={18} />}
            title={t('notifications.loadFailed')}
            action={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  void preferences.refetch();
                }}
              >
                {t('notifications.retry')}
              </Button>
            }
          />
        }
      >
        {(data) => {
          const groups = groupPreferences(data);
          const enabledCount = data.preferences.filter((pref) => pref.enabled).length;
          return (
            <div
              className="flex flex-col gap-4"
              style={{
                padding: 'var(--vq-space-5)',
                background: 'var(--vq-paper-raised)',
                border: '1px solid var(--vq-rule)',
                borderRadius: 'var(--vq-radius-lg)',
              }}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="info" variant="soft" size="sm" numeric>
                    {t('notifications.kindCount', { count: groups.length })}
                  </Badge>
                  <Badge tone="success" variant="soft" size="sm" numeric>
                    {t('notifications.enabledCount', {
                      count: enabledCount,
                      total: data.preferences.length,
                    })}
                  </Badge>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  iconLeft={<IcnRefresh size={14} />}
                  loading={preferences.isFetching && !preferences.isPending}
                  onClick={() => {
                    void preferences.refetch();
                  }}
                >
                  {t('notifications.refresh')}
                </Button>
              </div>

              <div className="flex flex-col">
                {groups.map((group) => (
                  <SettingRow
                    key={group.kind}
                    label={
                      <span className="flex min-w-0 flex-wrap items-center gap-2">
                        <span>
                          {t(`notifications.kinds.${group.kind}`, {
                            defaultValue: humanizeNotificationKind(group.kind),
                          })}
                        </span>
                        {group.schemaVersion !== null ? (
                          <Badge tone="neutral" variant="soft" size="sm">
                            {t('notifications.schemaVersion', {
                              version: group.schemaVersion,
                            })}
                          </Badge>
                        ) : null}
                      </span>
                    }
                    description={notificationKindDescription(group.kind)}
                  >
                    <div className="flex flex-wrap justify-end gap-3">
                      {CHANNELS.map((channel) => {
                        const pref = group.channels[channel];
                        if (pref === undefined) return null;
                        return (
                          <div
                            key={channel}
                            className="flex min-w-[164px] flex-col items-end gap-2"
                          >
                            <Switch
                              size="sm"
                              checked={pref.enabled}
                              disabled={update.isPending}
                              label={
                                <span className="inline-flex flex-wrap items-center gap-1.5">
                                  {channelIcon(channel)}
                                  {t(`notifications.channels.${channel}`)}
                                  {channel === 'push' && !pushState.supported ? (
                                    <Badge tone="warning" variant="soft" size="sm">
                                      {t('notifications.unavailableBadge')}
                                    </Badge>
                                  ) : null}
                                </span>
                              }
                              onCheckedChange={(checked) => {
                                void updatePreference(pref, { enabled: checked });
                              }}
                            />
                            {channel === 'in-app' ? null : (
                              <Select<DigestCadence>
                                value={pref.digest}
                                onValueChange={(digest) => {
                                  void updatePreference(pref, {
                                    enabled: pref.enabled,
                                    digest,
                                  });
                                }}
                                options={digestOptions}
                                size="sm"
                                aria-label={t('notifications.deliveryMode', {
                                  channel: t(`notifications.channels.${channel}`),
                                })}
                                disabled={update.isPending}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </SettingRow>
                ))}
              </div>

              <StatusBar
                aria-label={t('notifications.deliveryNoteLabel')}
                left={
                  <StatusBar.Item tone="info" icon={<IcnAlert size={12} />}>
                    {t('notifications.digestNote')}
                  </StatusBar.Item>
                }
              />
            </div>
          );
        }}
      </DataView>

      <NotificationDigestPreview />
    </section>
  );
}

function groupPreferences(data: {
  kinds: readonly { kind: string; schemaVersion: 1 }[];
  preferences: readonly NotificationPreference[];
}): PreferenceGroup[] {
  const versions = new Map(data.kinds.map((kind) => [kind.kind, kind.schemaVersion] as const));
  const kinds = new Set<string>(data.kinds.map((kind) => kind.kind));
  for (const pref of data.preferences) kinds.add(pref.kind);
  return [...kinds].sort().map((kind) => {
    const channels: PreferenceGroup['channels'] = {};
    for (const pref of data.preferences) {
      if (pref.kind === kind) channels[pref.channel] = pref;
    }
    return {
      kind,
      schemaVersion: versions.get(kind) ?? null,
      channels,
    };
  });
}

function channelIcon(channel: Channel): ReactNode {
  if (channel === 'email') return <IcnMail size={14} />;
  if (channel === 'push') return <IcnZap size={14} />;
  return <IcnBell size={14} />;
}
